import { useEffect, useState } from 'react'
import { Button } from 'primereact/button'

import { MeasurementType, RecordManualMeasurementDto } from '../../_generated/api/tagged-hardware'
import DialogContainer, { DialogContainerProps } from '../common/pure/DialogContainer'
import { Column, Row } from '../common/pure/FlexContainer'
import Heading from '../common/pure/Heading'
import PrimeIcon from '../common/pure/PrimeIcon'
import { ElongationIcon, UltraSonicLengthIcon } from '../common/inline-svg'
import { DropdownField, NumberField, TextareaField, TextField } from '../common/pure/input-fields'
import { IconToggleCard } from '../common/pure/ToggleCard'
import Bolt from '../../models/Bolt'
import Measurement from '../../models/Measurement'
import { formatDateTime, mm, toDecimalPlaces } from '../../tools/format'
import { MeasurementTypeSettings } from '../../tools/settings'
import t, { _ta } from '../../tools/t'

interface CreateManualMeasurementDialogProps extends DialogContainerProps {
    bolt: Bolt
    measurement?: Measurement
    onSubmit?: (data: RecordManualMeasurementDto) => Promise<void> | void
}

const ManualMeasurementStep = {
    BasedOn: 'basedOn',
    Result: 'result',
    Type: 'type',
    Comment: 'comment',
    Summary: 'summary',
}

const APPROX_TYPE_STRING_LENGTH = 16
const getMeasurementFormat = (m?: Measurement) => {
    if (!m) return ''
    const measuredAt = m.measuredAt ? formatDateTime(m.measuredAt) : t('common_unknown')
    const result = m.normalized?.resultMm
    const resultString = typeof result === 'number' ? mm(toDecimalPlaces(result)) : '-'
    const typeString = _ta(m, 'type').padEnd(APPROX_TYPE_STRING_LENGTH)
    return `${measuredAt}\t${typeString}\t\t${resultString}`
}

export default function CreateManualMeasurementDialog({
    bolt,
    measurement,
    onSubmit,
    onHide,
    ...props
}: CreateManualMeasurementDialogProps) {
    const isCloning = !!measurement
    const steps = [
        ManualMeasurementStep.BasedOn,
        ManualMeasurementStep.Type,
        ManualMeasurementStep.Result,
        ManualMeasurementStep.Comment,
        ManualMeasurementStep.Summary,

        // NOTE: Disable type selection step for cloned measurement.
    ].filter((s) => !isCloning || s !== ManualMeasurementStep.Type)

    const [step, setStep] = useState(steps[0])
    const [basedOnMeasurement, setBasedOnMeasurement] = useState(measurement)
    const [newMeasurementType, setNewMeasurementType] = useState(measurement?.type)
    const basedOnResult = basedOnMeasurement?.normalized?.resultMm
    const previousResult = basedOnResult ? toDecimalPlaces(basedOnResult) : undefined
    const [newResult, setNewResult] = useState<number>()
    const [deltaResult, setDeltaResult] = useState(0)
    const [comment, setComment] = useState('')

    useEffect(() => {
        if (isCloning) setDelta(0)
        else setNewResult(undefined)
    }, [newMeasurementType])

    const measurements = bolt?.measurements ?? []

    const stepIndex = steps.indexOf(step)
    const isFirstStep = stepIndex === 0
    const isLastStep = stepIndex === steps.length - 1

    const isDeltaResultValid = typeof deltaResult === 'number'
    const isNewResultValid = typeof newResult === 'number'
    const isMeasurementValid =
        !!basedOnMeasurement &&
        !!newMeasurementType &&
        !!comment &&
        (isNewResultValid || isDeltaResultValid)

    const isStepValid = (() => {
        switch (step) {
            case ManualMeasurementStep.BasedOn:
                return !!basedOnMeasurement
            case ManualMeasurementStep.Type:
                return !!newMeasurementType
            case ManualMeasurementStep.Comment:
                return !!comment
            case ManualMeasurementStep.Result:
                if (isCloning) return isDeltaResultValid
                return isNewResultValid
            default:
                return true
        }
    })()

    const measurementOptions = measurements.map((measurement) => ({
        label: getMeasurementFormat(measurement),
        value: measurement,
    }))

    const measurementTypeLabel = MeasurementTypeSettings.find(
        (option) => option.value === newMeasurementType
    )?.label

    // Perform decimal formatting on the calulated result to avoid floating point imprecision sneaking into the display.
    const prettyNewResult = isNewResultValid ? toDecimalPlaces(newResult) : undefined

    const hide = () => {
        onHide?.()
        setStep(steps[0])
        setNewResult(undefined)
        setDeltaResult(0)
        setNewMeasurementType(undefined)
        setBasedOnMeasurement(undefined)
    }

    const setDelta = (value: number) => {
        setDeltaResult(value)
        const result = (previousResult ?? 0) + value
        setNewResult(result)
    }

    const submit = async () => {
        if (!isMeasurementValid || !basedOnMeasurement || !newMeasurementType) return
        if (typeof newResult !== 'number') return
        const data: RecordManualMeasurementDto = {
            basedOnMeasurementId: basedOnMeasurement?.id,
            nextMeasurementType: newMeasurementType,
            newResult,
            comment,
        }
        await onSubmit?.(data)
        hide()
    }

    const nextStep = () => {
        if (!isStepValid) return
        if (stepIndex === steps.length) return
        setStep(steps[stepIndex + 1])
    }

    const prevStep = () => {
        if (stepIndex <= 0) return
        setStep(steps[stepIndex - 1])
    }

    const confirmStep = isLastStep ? submit : nextStep
    const undoStep = isFirstStep ? hide : prevStep

    return (
        <DialogContainer
            {...props}
            size="medium"
            className="manual-measurement-dialog flow-dialog"
            onHide={hide}
        >
            <Heading size={3} className="flow-dialog__title">
                {(() => {
                    switch (step) {
                        case ManualMeasurementStep.BasedOn:
                            return isCloning
                                ? t('manual_measurement_based_on_title')
                                : t('manual_measurement_based_on_step_title')
                        case ManualMeasurementStep.Type:
                            return t('manual_measurement_new_type_step_title')
                        case ManualMeasurementStep.Result:
                            return t('manual_measurement_new_result_step_title')
                        case ManualMeasurementStep.Comment:
                            return t('manual_measurement_comment_step_title')
                        case ManualMeasurementStep.Summary:
                            return t('manual_measurement_summary_step_title')
                    }
                })()}
            </Heading>
            <Column className="flow-dialog__content" gap={1}>
                {(() => {
                    switch (step) {
                        case ManualMeasurementStep.BasedOn:
                            return (
                                <DropdownField
                                    placeholder={t('measurement_field_adjust_measurement')}
                                    value={basedOnMeasurement}
                                    options={measurementOptions}
                                    onChange={(event) => setBasedOnMeasurement(event.value)}
                                    disabled={isCloning}
                                />
                            )
                        case ManualMeasurementStep.Type:
                            return (
                                <Row gap={1} alignment="center" arrangement="center" wide>
                                    {MeasurementTypeSettings.map((option) => (
                                        <IconToggleCard
                                            key={option.value}
                                            icon={
                                                option.value === MeasurementType.Reference ? (
                                                    <UltraSonicLengthIcon />
                                                ) : (
                                                    <ElongationIcon />
                                                )
                                            }
                                            label={option.label}
                                            selected={newMeasurementType === option.value}
                                            onClick={() =>
                                                setNewMeasurementType(
                                                    option.value as MeasurementType
                                                )
                                            }
                                        />
                                    ))}
                                </Row>
                            )
                        case ManualMeasurementStep.Result:
                            return (
                                <Column gap={1}>
                                    <NumberField
                                        placeholder={t('manual_measurement_previous_result_field')}
                                        unit={t('common_unit_mm')}
                                        allowDecimals
                                        value={previousResult}
                                        disabled
                                    />
                                    {isCloning && (
                                        <NumberField
                                            placeholder={t('measurement_field_result_delta')}
                                            unit={t('common_unit_mm')}
                                            allowDecimals
                                            value={deltaResult}
                                            onChange={(event) => setDelta(event.value ?? 0)}
                                        />
                                    )}
                                    <NumberField
                                        className={
                                            isCloning
                                                ? 'manual-measurement-dialog__new-result'
                                                : undefined
                                        }
                                        placeholder={t('manual_measurement_new_result_field')}
                                        unit={t('common_unit_mm')}
                                        allowDecimals
                                        value={prettyNewResult}
                                        onChange={(event) => setNewResult(event.value ?? undefined)}
                                        disabled={isCloning}
                                    />
                                </Column>
                            )
                        case ManualMeasurementStep.Comment:
                            return (
                                <TextareaField
                                    autoResize={false}
                                    approxRows={8}
                                    placeholder={t('common_comment')}
                                    value={comment}
                                    onChange={(event) => setComment(event.target.value)}
                                />
                            )
                        case ManualMeasurementStep.Summary:
                            return (
                                <Column className="manual-measurement-dialog__summary" gap={1}>
                                    <ManualMeasurementSummaryHeading
                                        title={t('manual_measurement_based_on_title')}
                                    />
                                    <TextField
                                        placeholder={t('measurement_field_adjust_measurement')}
                                        value={getMeasurementFormat(basedOnMeasurement)}
                                        disabled
                                    />

                                    <ManualMeasurementSummaryHeading
                                        title={t('manual_measurement_summary_heading_result')}
                                    />
                                    <TextField
                                        placeholder={t('measurement_field_type')}
                                        value={measurementTypeLabel}
                                        disabled
                                    />
                                    {isCloning ? (
                                        <Row gap={1} alignment="center">
                                            <NumberField
                                                placeholder={t(
                                                    'manual_measurement_previous_result_field'
                                                )}
                                                unit={t('common_unit_mm')}
                                                allowDecimals
                                                value={previousResult}
                                                disabled
                                            />
                                            <PrimeIcon name="plus" />

                                            <NumberField
                                                placeholder={t('measurement_field_result_delta')}
                                                unit={t('common_unit_mm')}
                                                allowDecimals
                                                value={deltaResult ?? undefined}
                                                disabled
                                            />
                                            <PrimeIcon name="equals" />
                                            <NumberField
                                                className="manual-measurement-dialog__new-result"
                                                placeholder={t(
                                                    'manual_measurement_new_result_field'
                                                )}
                                                unit={t('common_unit_mm')}
                                                allowDecimals
                                                value={prettyNewResult ?? undefined}
                                                disabled
                                            />
                                        </Row>
                                    ) : (
                                        <NumberField
                                            className="manual-measurement-dialog__new-result"
                                            placeholder={t('manual_measurement_new_result_field')}
                                            unit={t('common_unit_mm')}
                                            allowDecimals
                                            value={prettyNewResult ?? undefined}
                                            disabled
                                        />
                                    )}
                                    <ManualMeasurementSummaryHeading
                                        title={t('manual_measurement_summary_heading_comment')}
                                    />
                                    <TextareaField
                                        autoResize={false}
                                        approxRows={6}
                                        placeholder={t('common_comment')}
                                        value={comment}
                                        onChange={(event) => setComment(event.target.value)}
                                    />
                                </Column>
                            )
                        default:
                            return null
                    }
                })()}
            </Column>
            <Row alignment="center" arrangement="spaced" wide>
                <Button
                    onClick={undoStep}
                    label={isFirstStep ? t('common_cancel') : t('common_prev')}
                    severity="secondary"
                    text
                />
                <Button
                    onClick={confirmStep}
                    label={isLastStep ? t('common_create') : t('common_next')}
                    disabled={isLastStep ? !isMeasurementValid : !isStepValid}
                />
            </Row>
        </DialogContainer>
    )
}

interface ManualMeasurementSummaryHeadingProps {
    title: string
}
const ManualMeasurementSummaryHeading = ({ title }: ManualMeasurementSummaryHeadingProps) => (
    <div className="manual-measurement-dialog__summary-heading">{title}</div>
)
